<template>
    <div class="px-8 lg:grid lg:grid-cols-12 lg:gap-x-10">
        <section class="lg:col-span-4">
            <div class="bg-white pt-6 shadow sm:rounded-md sm:overflow-hidden">
                <div class="px-4 sm:px-6">
                    <h2 class="text-lg leading-6 font-medium text-gray-900">Current Month Balance</h2>
                </div>
                <div class="px-6 py-4 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider mt-6">
                    {{ currentDate() }}
                </div>
                <div class="flex items-center p-8">
                    <div class="flex-shrink-0">
                        <svg class="h-6 w-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                        </svg>
                    </div>
                    <div class="ml-5 w-0 flex-1">
                        <dl>
                            <dd>
                                <div class="text-2xl text-blue-900 font-medium text-gray-900">
                                    ${{ balance.current_balance | currency }}
                                </div>
                            </dd>
                        </dl>
                    </div>
                </div>
            </div>
        </section>
        <section class="lg:col-span-8">
            <div class="bg-white pt-6 shadow sm:rounded-md sm:overflow-hidden">
                <div class="px-4 sm:px-6">
                    <h2 class="text-lg leading-6 font-medium text-gray-900">Daily Activity for the Month</h2>
                </div>
                <div class="flex flex-col mt-6">
                    <div class="-my-2 sm:-mx-6 lg:-mx-8">
                        <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                            <div class="shadow border-b border-gray-200 sm:rounded-lg">
                                <table class="min-w-full divide-y divide-gray-200">
                                    <thead class="bg-gray-50">
                                    <tr>
                                        <table-head display="Transaction Date" key-name="transaction_date" :sort-key="sortKey" :sort-order="sortOrder" />
                                        <table-head display="Reference Number" key-name="reference" :sort-key="sortKey" :sort-order="sortOrder"  />
                                        <table-head display="Description" key-name="description" :sort-key="sortKey" :sort-order="sortOrder"  />
                                        <table-head display="Transaction Amount" key-name="transaction_amount" :sort-key="sortKey" :sort-order="sortOrder"  />
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="(item, idx) in sortedItems">
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {{ item.transaction_date | niceDate }}
                                        </td>
                                        <td class="px-6 py-4 text-gray-500 text-sm">
                                            {{ item.reference }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {{ item.description }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {{ item.transaction_amount | currency }}
                                        </td>

                                    </tr>
                                    </tbody>
                                </table>
                                <div v-if="items.length === 0" class="text-center text-gray-400 text-lg p-8">No current items...</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>


<script>

import TableHead from "@/Components/DataTable/TableHead";

export default {
    components: {TableHead},
    props: {
        balance: {
            type: Object,
            default: {
                current_balance: 0
            }
        },
        items: {
            type: Array,
            default: []
        },
    },
    data() {
        return {
            filteredItems: [],
            showFilter: false,
            sortKey: 'transaction_date',
            sortOrder: 'desc',
            searchOptions: {}
        }
    },
    mounted() {
        this.filteredItems = this.items
        this.$eventBus.$on('sortBy', (keyName) => {
            this.sortBy(keyName)
        })
    },
    methods: {
        setShowMenu(id) {
            this.showMenu = this.showMenu === id ? null : id
        },
        sortBy(key) {
            this.sortKey = key
            this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
        },
        compareValues(key, order = 'asc') {
            return function innerSort(a, b) {
                if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) return 0;
                const comparison = a[key].toString().localeCompare(b[key], undefined, {numeric: true});
                return (
                    (order === 'desc') ? (comparison * -1) : comparison
                );
            };
        },
        currentDate() {
            let dateObj = new Date()
            let monthName = dateObj.toLocaleString("default", { month: "long" })
            let year = dateObj.getFullYear()
            return monthName + ' ' + year
        }
    },
    computed: {
        sortedItems() {
            return this.filteredItems.sort(
                this.compareValues(this.sortKey, this.sortOrder)
            )
        }
    },
    watch: {
        searchOptions: {
            deep: true,
            handler() {
                this.filteredItems = this.items
                if (this.searchOptions.minTktNum) {
                    this.filteredItems = this.filteredItems.filter(item => item.load_ticket_number >= parseInt(this.searchOptions.minTktNum))
                }
                if (this.searchOptions.maxTktNum) {
                    this.filteredItems = this.filteredItems.filter(item => item.load_ticket_number <= parseInt(this.searchOptions.maxTktNum))
                }
                if (this.searchOptions.minTktDate) {
                    this.filteredItems = this.filteredItems.filter(item => new Date(item.load_ticket_date) >= new Date(this.searchOptions.minTktDate))
                }
                if (this.searchOptions.maxTktDate) {
                    this.filteredItems = this.filteredItems.filter(item => new Date(item.load_ticket_date) <= new Date(this.searchOptions.maxTktDate))
                }
            }
        }
    }
}
</script>
